/**
 * CouncilStrategy Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * An interactive strategic pillar visualization for the Irish Funds Council strategy.
 * Features include:
 * - Four strategic pillars: Advocacy, Capability & Innovation, Talent, and Engagement & Promotion
 * - Foundation layer with core strategic elements
 * - Interactive topic cards with hover tooltips
 * - Modal integration for detailed topic information
 * - Data-driven content organization and grouping
 * - IFIA brand styling with pillar-specific color schemes
 * - Responsive design with professional layout
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of strategy data objects with pillar, topic, and description information
 * @returns {JSX.Element} Strategic pillar visualization component
 */
import React, { useState } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './CouncilStrategy.css';
import Parse from 'html-react-parser';
import CouncilsStrategyModal from '../../modals/CouncilsStrategyModal';

/**
 * CouncilStrategy Component
 * 
 * Displays the Irish Funds Council strategic pillars with interactive topic exploration.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Strategy data array
 * @returns {JSX.Element} Council strategy component
 */
const CouncilStrategy = ({ data }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedColour, setSelectedColour] = useState(null);
    const [topPillar, setTopPillar] = useState(null);

    const groupedData = data.reduce((acc, item) => {
        if (item.pillar === 'Pillar') {
            if (!acc[item.strategicPillar]) {
                acc[item.strategicPillar] = [];
            }
            acc[item.strategicPillar].push(item);
        } else if (item.pillar === 'Foundation') {
            if (!acc['Foundation']) {
                acc['Foundation'] = [];
            }
            acc['Foundation'].push(item);
        }
        return acc;
    }, {});

    /**
     * Generates tooltip content for strategy items
     * 
     * Prioritizes description over strategic objective and truncates long content.
     * 
     * @param {Object} item - Strategy item with description and/or strategicObjective
     * @returns {JSX.Element|string} Parsed HTML content or fallback text
     */
    const getTippyContent = (item) => {
        if (item.description) {
            return Parse(item.description);
        }
        if (item.strategicObjective) {
            const truncatedObjective = item.strategicObjective.length > 600 
                ? item.strategicObjective.substring(0, 600) + '...'
                : item.strategicObjective;
            return Parse(truncatedObjective);
        }
        return 'No description available';
    };

    return (
        <div className="council-strategy">
            <div className="pillars-container">
                {/* Advocacy Pillar */}
                <div className="pillar advocacy">
                    <div className="pillar-header">Advocacy</div>
                    <div className="pillar-topics">
                        {groupedData['Advocacy']?.map(item => (
                            <Tippy key={item.id} content={getTippyContent(item)}>
                                <div onClick={() => { setSelectedTopic(item); setShowModal(true); setSelectedColour("advocacy"); setTopPillar("Advocacy"); }} className="topic">{item.topic}</div>
                            </Tippy>
                        ))}
                    </div>
                </div>

                {/* Capability & Innovation Pillar */}
                <div className="pillar capability">
                    <div className="pillar-header">Capability & Innovation</div>
                    <div className="pillar-topics">
                        {groupedData['Capability & Innovation']?.map(item => (
                            <Tippy key={item.id} content={getTippyContent(item)}>
                                <div onClick={() => { setSelectedTopic(item); setShowModal(true); setSelectedColour("capability"); setTopPillar("Capability & Innovation"); }} className="topic">{item.topic}</div>
                            </Tippy>
                        ))}
                    </div>
                </div>

                {/* Talent Pillar */}
                <div className="pillar talent">
                    <div className="pillar-header">Talent</div>
                    <div className="pillar-topics">
                        {groupedData['Talent']?.map(item => (
                            <Tippy key={item.id} content={getTippyContent(item)}>
                                <div onClick={() => { setSelectedTopic(item); setShowModal(true); setSelectedColour("talent"); setTopPillar("Talent"); }} className="topic">{item.topic}</div>
                            </Tippy>
                        ))}
                    </div>
                </div>

                {/* Engagement & Promotion Pillar */}
                <div className="pillar engagement">
                    <div className="pillar-header">Engagement & Promotion</div>
                    <div className="pillar-topics">
                        {groupedData['Engagement & Promotion']?.map(item => (
                            <Tippy key={item.id} content={getTippyContent(item)}>
                                <div onClick={() => { setSelectedTopic(item); setShowModal(true); setSelectedColour("engagement"); setTopPillar("Engagement & Promotion"); }} className="topic">{item.topic}</div>
                            </Tippy>
                        ))}
                    </div>
                </div>
            </div>

            {/* Foundation section */}
            <div className="foundation">
                {groupedData['Foundation']?.map(item => (
                    <Tippy key={item.id} content={getTippyContent(item)}>
                        <div className="foundation-topic">{item.topic}</div>
                    </Tippy>
                ))}
            </div>

           
            {showModal && (
                <CouncilsStrategyModal 
                    data={selectedTopic ? [selectedTopic] : []} 
                    title={selectedTopic ? selectedTopic.topic : 'Topic Details'} 
                    show={showModal} 
                    onClose={() => {
                        setShowModal(false);
                        setSelectedTopic(null);
                    }} 
                    selectedColour={selectedColour}
                    topPillar={topPillar}
                />
            )}
        </div>
    );
};

export default CouncilStrategy;